'use client';

import React from 'react';
import { OutstandingBalanceModal } from '../components/OutstandingBalanceModal';
import { useAuth } from '../context/AuthContext';

import { PaymentModal } from '../components/PaymentModal';
import { BillingHistory } from '../components/BillingHistory';
import { SettingsModal } from '../components/SettingsModal';
import { ApiUsageStats } from '../components/ApiUsageStats';
import { RefillModal } from '../components/RefillModal';
import { Settings, CreditCard, Key, ImageIcon, Zap, ArrowRight, Copy, Plus, Eye, EyeOff, Check, AlertTriangle, Trash2, RefreshCw, XCircle } from 'lucide-react';
import { collection, query, orderBy, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { Link } from 'react-router-dom';
function SecurityWarningModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-background rounded-xl w-full max-w-md shadow-2xl p-6 scale-100 animate-in zoom-in-95 duration-200">
                <div className="flex flex-col items-center text-center">
                    <div className="p-3 bg-red-500/10 rounded-full mb-4">
                        <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Reveal API Key?</h3>
                    <p className="text-muted-foreground mb-6 text-sm">
                        Your API key provides full access to your account.
                        <br />
                        <span className="font-semibold text-red-600 dark:text-red-400 mt-2 block">Do not share this key with anyone.</span>
                        Only share with trusted partners.
                    </p>
                    <div className="flex gap-4 w-full">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted font-medium transition-colors text-foreground"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                            className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                        >
                            Reveal Key
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}



function DeleteKeyWarningModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-background rounded-xl w-full max-w-md shadow-2xl p-6 scale-100 animate-in zoom-in-95 duration-200">
                <div className="flex flex-col items-center text-center">
                    <div className="p-3 bg-red-500/10 rounded-full mb-4">
                        <Trash2 className="h-8 w-8 text-red-600 dark:text-red-500" />
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-2">Delete API Key?</h3>
                    <p className="text-muted-foreground mb-6 text-sm">
                        Are you sure you want to delete your API key? You will lose API access immediately.
                    </p>
                    <div className="flex gap-4 w-full">
                        <button onClick={onClose} className="flex-1 px-4 py-2 rounded-lg border border-border hover:bg-muted font-medium transition-colors text-foreground">Cancel</button>
                        <button onClick={onConfirm} className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors">Delete Key</button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function Dashboard() {
    const { user, isPro, regenerateApiKey, deleteApiKey, settleBalance } = useAuth();
    const [copied, setCopied] = React.useState(false);
    const [showApiKey, setShowApiKey] = React.useState(false);
    const [isPaymentModalOpen, setIsPaymentModalOpen] = React.useState(false);
    const [isBalanceModalOpen, setIsBalanceModalOpen] = React.useState(false);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
    const [isWarningModalOpen, setIsWarningModalOpen] = React.useState(false);
    const [isDeleteKeyModalOpen, setIsDeleteKeyModalOpen] = React.useState(false);
    const [isRefillModalOpen, setIsRefillModalOpen] = React.useState(false);
    const [payments, setPayments] = React.useState<any[]>([]);

    const handleToggleKey = () => {
        if (showApiKey) {
            setShowApiKey(false);
        } else {
            setIsWarningModalOpen(true);
        }
    };

    React.useEffect(() => {
        if (user?.userId) {
            const fetchPayments = async () => {
                try {
                    const q = query(
                        collection(db, 'bgusers', user.userId, 'payments'),
                        orderBy('date', 'desc')
                    );
                    const querySnapshot = await getDocs(q);
                    const paymentData = querySnapshot.docs.map((doc: any) => ({
                        id: doc.id,
                        ...doc.data(),
                        date: doc.data().date?.toDate() || new Date(),
                        expiryDate: doc.data().expiryDate?.toDate()
                    }));
                    setPayments(paymentData);
                } catch (error) {
                    console.error("Error fetching payments:", error);
                }
            };
            fetchPayments();
        }
    }, [user?.userId, user?.credits]);

    const handleSettleBalance = (amount: number) => {
        if (!user) return;

        // Gateways have a $1 minimum for USD. For smaller amounts, we use INR for precision.
        const useINR = amount < 1;
        const finalAmount = useINR
            ? Math.round(amount * 85 * 100) // Convert to INR Paise
            : Math.round(amount * 100);     // Keep as USD Cents

        const options = {
            key: import.meta.env.VITE_RAZORPAY_KEY_ID,
            amount: finalAmount,
            currency: useINR ? 'INR' : 'USD',
            name: 'BgRemover',
            description: 'Balance Settlement',
            handler: async (response: any) => {
                try {
                    await settleBalance(response.razorpay_payment_id, amount);
                    // Refresh payments list
                    window.location.reload();
                } catch (error) {
                    console.error("Dashboard: Settlement failed", error);
                    alert("Settlement recording failed. Please contact support.");
                }
            },
            prefill: {
                name: user.displayName || '',
                email: user.email || '',
            },
            theme: {
                color: '#000000',
            },
        };

        const rzp = new (window as any).Razorpay(options);
        rzp.open();
    };

    const copyApiKey = () => {
        if (user?.apiKey) {
            navigator.clipboard.writeText(user.apiKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div className="min-h-screen bg-background py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-foreground tracking-tight">Dashboard</h1>
                        <p className="text-muted-foreground mt-1">
                            Welcome back, <span className="font-medium text-foreground">{user?.displayName?.split(' ')[0] || 'Creator'}</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {isPro && (
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">
                                <Zap className="h-3 w-3" />
                                {user?.planName || 'Lifetime Membership'} Active
                            </div>
                        )}
                        <button
                            onClick={() => setIsSettingsModalOpen(true)}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-border hover:bg-muted text-sm font-medium text-foreground transition-colors"
                        >
                            <Settings className="h-4 w-4" /> Settings
                        </button>
                    </div>
                </div>

                {/* Main Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
                    {/* API Key Section */}
                    <div className="lg:col-span-8 bg-card border border-border rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                                <Key className="h-4 w-4 text-muted-foreground" />
                                API Configuration
                            </h3>
                        </div>

                        {user?.apiKey ? (
                            <div className="space-y-4">
                                <div className="flex gap-3">
                                    <div className="flex-1 relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Key className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                        <input
                                            type="text"
                                            readOnly
                                            value={showApiKey ? user.apiKey : '•'.repeat(24) + user.apiKey.slice(-4)}
                                            className="block w-full pl-10 pr-12 py-2 sm:text-sm border border-border rounded-md bg-muted/30 text-foreground font-mono"
                                        />
                                        <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                                            <button
                                                onClick={handleToggleKey}
                                                className="p-1.5 text-muted-foreground hover:text-foreground rounded-md hover:bg-muted transition-colors"
                                            >
                                                {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                    </div>
                                    <button
                                        onClick={copyApiKey}
                                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                                    >
                                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                        <span className="ml-2">{copied ? 'Copied' : 'Copy'}</span>
                                    </button>
                                </div>
                                <div className="flex gap-4 pt-2">
                                    {!isPro && (
                                        <button
                                            onClick={() => setIsDeleteKeyModalOpen(true)}
                                            className="text-xs font-medium text-red-500 hover:text-red-600 transition-colors flex items-center gap-1"
                                        >
                                            <Trash2 className="h-3 w-3" /> Revoke
                                        </button>
                                    )}
                                </div>
                                {typeof user.credits === 'number' && user.credits <= 0 && user.planType === 'credit_pack' && (
                                    <div className="mt-3 bg-red-500/10 border border-red-500/20 rounded-md p-3 flex items-start gap-2">
                                        <XCircle className="h-4 w-4 text-red-600 dark:text-red-500 mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-sm font-medium text-red-800 dark:text-red-300">API Access Paused</p>
                                            <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
                                                You have 0 credits. The API will return errors until you refill.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="text-center py-8 bg-muted/20 rounded-lg border border-dashed border-border">
                                <p className="text-sm text-muted-foreground mb-4">No API key found used for integration.</p>
                                <button
                                    onClick={() => setIsPaymentModalOpen(true)}
                                    className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary/90"
                                >
                                    <Plus className="h-4 w-4" /> Generate API Key
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Plan Status */}
                    <div className="lg:col-span-4 bg-card border border-border rounded-lg shadow-sm p-6 flex flex-col justify-between">
                        <div>
                            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-muted-foreground" />
                                {user?.planType === 'credit_pack' ? 'Credit Pack' : 'Subscription'}
                            </h3>

                            {user?.planType === 'credit_pack' ? (
                                <>
                                    <div className="mb-4">
                                        <div className="flex items-baseline justify-between mb-2">
                                            <span className="text-3xl font-bold text-foreground">
                                                {typeof user.credits === 'number' ? user.credits : 0}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                / {user.maxCredits || 0} credits
                                            </span>
                                        </div>
                                        <div className="w-full bg-muted/30 rounded-full h-2 mb-2">
                                            <div
                                                className="bg-primary h-2 rounded-full transition-all duration-300"
                                                style={{
                                                    width: `${Math.min(100, ((typeof user.credits === 'number' ? user.credits : 0) / (user.maxCredits || 1)) * 100)}%`
                                                }}
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">
                                            {typeof user.credits === 'number' && user.credits === 0
                                                ? 'Credits depleted - refill to continue'
                                                : 'Credits remaining'}
                                        </p>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {typeof user.credits === 'number' && user.credits < 10 && (
                                            <>
                                                <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 mb-2 flex items-start gap-3">
                                                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <h4 className="text-sm font-medium text-amber-800 dark:text-amber-400">Low Balance Warning</h4>
                                                        <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                                            You have less than 10 credits remaining. Refill now or your API access will stop.
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => setIsRefillModalOpen(true)}
                                                    className="w-full py-2.5 px-4 border-transparent bg-amber-600 hover:bg-amber-700 text-white rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                    Refill Credits Now
                                                </button>
                                            </>
                                        )}
                                        {!(typeof user.credits === 'number' && user.credits < 10) && (
                                            <button
                                                onClick={() => setIsRefillModalOpen(true)}
                                                className="w-full py-2.5 px-4 border-transparent bg-primary text-primary-foreground hover:bg-primary/90 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2"
                                            >
                                                <RefreshCw className="h-4 w-4" />
                                                Refill Credits
                                            </button>
                                        )}
                                        <button
                                            onClick={() => setIsPaymentModalOpen(true)}
                                            className="w-full py-2.5 px-4 border border-border bg-background hover:bg-muted text-foreground rounded-md text-sm font-medium transition-colors"
                                        >
                                            Switch Plan
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <div className="text-3xl font-bold text-foreground mb-1">
                                            {isPro ? (user?.planName || 'Lifetime Membership') : 'Free'}
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {isPro ? 'Pay-as-you-go billing' : 'Limited daily requests'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            if (isPro && (user?.billing?.currentBalance || 0) > 0) {
                                                setIsBalanceModalOpen(true);
                                            } else {
                                                setIsPaymentModalOpen(true);
                                            }
                                        }}
                                        className={`w-full py-2.5 px-4 border rounded-md text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all ${isPro && (user?.billing?.currentBalance || 0) > 0
                                            ? 'border-border bg-muted/50 text-muted-foreground hover:bg-muted font-bold'
                                            : isPro
                                                ? 'border-border bg-background hover:bg-muted text-foreground'
                                                : 'border-transparent bg-foreground text-background hover:bg-foreground/90'
                                            }`}
                                    >
                                        {isPro ? 'Manage Subscription' : 'Upgrade Plan'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Usage Stats - Full Width */}
                    {user?.apiKey && (user.credits === 'UNLIMITED' || (typeof user.credits === 'number' && user.credits > 0)) && (
                        <div className="lg:col-span-12">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-foreground">Usage Overview</h2>
                                <Link
                                    to="/logs"
                                    className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
                                >
                                    View Full Logs <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                            <ApiUsageStats />
                        </div>
                    )}
                </div>

                {/* Additional Sections Grid */}
                {/* Billing History Section */}
                <div className="mb-8">
                    <div className="bg-card border border-border rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                                <CreditCard className="h-4 w-4 text-muted-foreground" /> Billing History
                            </h3>
                            <Link
                                to="/billing"
                                className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1 transition-colors"
                            >
                                View Detailed Billing <ArrowRight className="h-4 w-4" />
                            </Link>
                        </div>
                        <BillingHistory
                            payments={payments}
                            onDelete={async (id) => {
                                try {
                                    if (user?.userId) {
                                        await deleteDoc(doc(db, 'bgusers', user.userId, 'payments', id));
                                        // Update local state
                                        setPayments(prev => prev.filter(p => p.id !== id));
                                    }
                                } catch (error) {
                                    console.error("Error deleting payment:", error);
                                    alert("Failed to delete payment record");
                                }
                            }}
                        />
                    </div>
                </div>



                {/* Modals remain the same */}
                <PaymentModal
                    isOpen={isPaymentModalOpen}
                    onClose={() => setIsPaymentModalOpen(false)}
                />
                <OutstandingBalanceModal
                    isOpen={isBalanceModalOpen}
                    onClose={() => setIsBalanceModalOpen(false)}
                    balance={user?.billing?.currentBalance || 0}
                    onPay={() => {
                        setIsBalanceModalOpen(false);
                        handleSettleBalance(user?.billing?.currentBalance || 0);
                    }}
                />
                <SettingsModal
                    isOpen={isSettingsModalOpen}
                    onClose={() => setIsSettingsModalOpen(false)}
                />
                <SecurityWarningModal
                    isOpen={isWarningModalOpen}
                    onClose={() => setIsWarningModalOpen(false)}
                    onConfirm={() => setShowApiKey(true)}
                />
                <DeleteKeyWarningModal
                    isOpen={isDeleteKeyModalOpen}
                    onClose={() => setIsDeleteKeyModalOpen(false)}
                    onConfirm={deleteApiKey}
                />
                <RefillModal
                    isOpen={isRefillModalOpen}
                    onClose={() => setIsRefillModalOpen(false)}
                    onRefill={() => {
                        setIsRefillModalOpen(false);
                        setIsPaymentModalOpen(true);
                    }}
                />
            </div>
        </div >
    );
}
